import React, { useState, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { TemplateType } from '../../../common/constants/project';
import type { Template } from '../../../common/constants/steps';
import { useProjectData } from '../../../controllers/import-state';
import messages from './messages';
import { TemplateModal, TemplateCard } from './template-modal';

type Props = {
	templates: Template[];
};

export const Templates = ({ templates }: Props) => {
	const [isOpen, setOpen] = useState(false);
	const { formatMessage } = useIntl();
	const [{ template }, { setProjectData }] = useProjectData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClose = useCallback(() => {
		setOpen(false);
	}, []);

	const onConfirm = useCallback(
		(value: TemplateType) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'x2cChangeTemplateConfirm',
				{ templateType: value },
			);
			setProjectData({
				template: value,
			});
			onClose();
		},
		[createAnalyticsEvent, setProjectData, onClose],
	);

	const onChangeTemplateClick = useCallback(() => {
		setOpen(true);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'x2cChangeTemplateLink',
		);
	}, [createAnalyticsEvent]);

	const defaultTemplate = useMemo(
		() => templates.find(({ isDefault }) => isDefault) || templates[0],
		[templates],
	);

	const selectedTemplate = useMemo(() => {
		const setTemplate = templates.find(({ key }) => template === key);
		if (setTemplate) {
			return setTemplate;
		}
		setProjectData({ template: defaultTemplate.key });
		return defaultTemplate;
	}, [template, defaultTemplate, templates, setProjectData]);

	const { title, description, iconUrl } = selectedTemplate;

	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.project-create-screen.templates.wrapper">
			<Header>
				<Heading>{formatMessage(messages.title)}</Heading>
				<Button appearance="link" onClick={onChangeTemplateClick}>
					{formatMessage(messages.button)}
				</Button>
			</Header>
			<TemplateCard
				iconUrl={iconUrl}
				title={<Title>{formatMessage(title)}</Title>}
				description={<Description>{formatMessage(description)}</Description>}
			/>
			<TemplateModal
				templates={templates}
				defaultTemplate={selectedTemplate.key}
				isOpen={isOpen}
				onClose={onClose}
				onConfirm={onConfirm}
			/>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	width: '100%',
	flexDirection: 'column',
	marginTop: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.div({
	fontSize: '14px',
	fontWeight: '600',
	lineHeight: '16px',
	textAlign: 'left',
	alignSelf: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	fontSize: '16px',
	fontWeight: '600',
	lineHeight: '20px',
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.div({
	fontSize: '11px',
	fontWeight: '400',
	lineHeight: '14px',
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
});
